import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Switch, Typography, Backdrop, CircularProgress } from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { useGestionEquipements } from './hooks/GestionEquipementsHook';

/**
 * Groups age distribution data into ranges for chart display.
 *
 * @param {Array<Object>} data - Array of objects representing equipment with age and count fields.
 * @returns {Array<Object>} Array of objects representing age ranges with aggregated counts.
 */
function rangeAges(data) {
  if (!data || data.length === 0) return [];
  // Find min and max age
  const ages = data.map((row) => Number(row.Age));
  const minAge = Math.min(...ages);
  const maxAge = Math.max(...ages);
  // Choose range size automatically
  const possibleRangeSizes = [1, 2, 3, 5, 10, 20];
  let rangeSize = 1;
  for (const size of possibleRangeSizes) {
    if (Math.ceil((maxAge - minAge + 1) / size) <= 35) {
      rangeSize = size;
      break;
    }
  }
  // Create ranges
  const ranges = [];
  for (let start = Math.floor(minAge / rangeSize) * rangeSize; start <= maxAge; start += rangeSize) {
    ranges.push({
      label: `${start}-${start + rangeSize - 1}`,
      Age: start + rangeSize - 1,
      Count_RealAge: 0,
      Count_ApparentAge: 0,
    });
  }
  // Fill ranges
  data.forEach((row) => {
    const age = Number(row.Age);
    const rangeIdx = Math.floor((age - Math.floor(minAge / rangeSize) * rangeSize) / rangeSize);
    if (ranges[rangeIdx]) {
      ranges[rangeIdx].Count_RealAge += Number(row.Count_RealAge);
      ranges[rangeIdx].Count_ApparentAge += Number(row.Count_ApparentAge);
    }
  });
  return ranges;
}

const GestionEquipements = () => {
  const { t } = useTranslation();
  const [showApparentAge, setShowApparentAge] = useState(false);
  const { ageDistribution, selectedFamily, isLoading } = useGestionEquipements();

  const chartData = useMemo(() => {
    if (!ageDistribution || ageDistribution.length === 0) return [];
    return rangeAges(ageDistribution);
  }, [ageDistribution]);

  if (!selectedFamily)
    return (
      <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
        {t('commoncomponents.equipment.wizard.messages.selectFamily', 'Please select an equipment family.')}
      </Typography>
    );

  if (isLoading)
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  if (chartData.length === 0)
    return (
      <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
        {t('commoncomponents.equipment.wizard.messages.noDataForFamily', 'No data available for the selected family.')}
      </Typography>
    );

  return (
    <div>
      <Grid container spacing={2} alignItems="center">
        {/* Switch pour changer entre Age Réel et Age Apparent */}
        <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
          <span>{t('commoncomponents.equipment.wizard.fields.realAge', 'Real Age')}</span>
          <Switch
            checked={showApparentAge}
            onChange={(e) => setShowApparentAge(e.target.checked)}
            color="primary"
            inputProps={{ 'aria-label': 'switch between real and apparent age distribution' }}
          />
          <span>{t('commoncomponents.equipment.wizard.fields.apparentAge', 'Apparent Age')}</span>
        </Grid>
      </Grid>
      <ResponsiveContainer width="100%" height={350} style={{ marginTop: '10px', paddingBottom: '10px' }}>
        <BarChart data={chartData} barCategoryGap="0%" barGap={0}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            type="category"
            tick={{ fontSize: 12, fill: '#888', dy: 6 }}
            tickLine={false}
            axisLine={{ stroke: '#ccc' }}
            interval={0}
            allowDecimals={false}
          />
          <YAxis
            label={{
              value: t('commoncomponents.equipment.wizard.fields.numberOfEquipment', 'Number of equipment'),
              angle: -90,
              position: 'insideLeft',
              offset: -15,
              style: {
                textAnchor: 'middle',
              },
            }}
            mirror={true}
            allowDecimals={false}
          />
          <Tooltip
            formatter={(value, name) => {
              const label = t('commoncomponents.equipment.wizard.fields.numberOfEquipment', 'Number of equipment');
              return [value, label];
            }}
            labelFormatter={(label) =>
              `${t('commoncomponents.equipment.wizard.fields.ageRange', 'Age range')}: ${label}`
            }
            cursor={true}
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
            itemStyle={{ color: '#000' }}
            labelStyle={{ color: '#000', fontWeight: 'bold' }}
          />
          <Legend iconSize={0} />
          <Bar
            dataKey={showApparentAge ? 'Count_ApparentAge' : 'Count_RealAge'}
            fill="#8884d8"
            name={
              showApparentAge
                ? t('commoncomponents.equipment.wizard.fields.apparentAge', 'Apparent Age')
                : t('commoncomponents.equipment.wizard.fields.realAge', 'Real Age')
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GestionEquipements;
